import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Clock, ArrowUpRight, Phone } from "lucide-react";
import { findPostBySlug, POSTS } from "@/data/posts";
import { PRACTICE } from "@/data/site";
import { useT } from "@/i18n/LanguageContext";
import SunDivider from "@/components/site/SunDivider";
import Seo from "@/components/site/Seo";
import { blogPostingSchema, breadcrumbSchema } from "@/lib/seo";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

/** Render a single body block — string or ["tag", ...] tuple */
function Block({ block }) {
  if (typeof block === "string") {
    return <p className="mt-5 text-[17px] leading-[1.75] text-[var(--text-main)]/85">{block}</p>;
  }
  const [tag, content] = block;
  if (tag === "h2") {
    return (
      <h2 className="font-serif-display text-[30px] md:text-[36px] leading-tight mt-10 mb-1 text-[var(--primary)]">
        {content}
      </h2>
    );
  }
  if (tag === "p") {
    return <p className="mt-5 text-[17px] leading-[1.75] text-[var(--text-main)]/85">{content}</p>;
  }
  if (tag === "ul") {
    return (
      <ul className="mt-5 space-y-2 list-disc pl-6 text-[16.5px] leading-[1.7] text-[var(--text-main)]/85 marker:text-[var(--accent)]">
        {content.map((li, i) => (
          <li key={i}>{li}</li>
        ))}
      </ul>
    );
  }
  return null;
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const { t } = useT();
  const post = findPostBySlug(slug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!post) return <Navigate to="/insights" replace />;

  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  return (
    <article data-testid={`blog-post-${post.slug}`}>
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/insights/${post.slug}`}
        type="article"
        image={post.cover.startsWith("http") ? post.cover : `https://primelogichub.com${post.cover}`}
        jsonLd={[
          blogPostingSchema(post),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Insights", path: "/insights" },
            { name: post.title, path: `/insights/${post.slug}` },
          ]),
        ]}
      />

      {/* Header */}
      <section className="px-6 md:px-10 lg:px-16 pt-14 md:pt-20 pb-8 max-w-4xl mx-auto">
        <Link
          to="/insights"
          data-testid="back-to-insights"
          className="font-mono-eyebrow text-[var(--text-muted)] hover:text-[var(--accent)]"
        >
          ← {t("All insights")}
        </Link>
        <div className="mt-6 flex flex-wrap items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
          {(post.tags || []).map((tag) => (
            <span
              key={tag}
              className="px-2.5 py-1 rounded-full bg-[var(--surface)] border border-[var(--border)] text-[var(--accent)]"
            >
              {tag}
            </span>
          ))}
          <span>·</span>
          <span>{formatDate(post.publishedAt)}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" strokeWidth={2} />
            {post.readMinutes} {t("min read")}
          </span>
        </div>
        <h1 className="font-serif-display text-[var(--primary)] mt-5 text-[40px] md:text-[64px] leading-[1.05] tracking-tight">
          {post.title}
        </h1>
        <p className="mt-5 text-[19px] text-[var(--text-muted)] leading-relaxed">
          {post.excerpt}
        </p>
        <div className="mt-6 text-sm text-[var(--text-main)]/85">
          {t("By")} <span className="font-medium">{post.author}</span>
        </div>
      </section>

      {/* Cover image */}
      <div className="px-6 md:px-10 lg:px-16 max-w-5xl mx-auto">
        <div className="aspect-[16/9] overflow-hidden rounded-3xl border border-[var(--border)]">
          <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </div>

      {/* Body */}
      <section className="px-6 md:px-10 lg:px-16 py-14 md:py-20 max-w-3xl mx-auto">
        {post.body.map((b, i) => (
          <Block key={i} block={b} />
        ))}
      </section>

      {/* CTA */}
      <section className="px-6 md:px-10 lg:px-16 py-16 bg-[var(--primary)] text-[var(--bg)]">
        <div className="max-w-3xl mx-auto text-center">
          <div className="font-mono-eyebrow text-[var(--accent)]">
            {t("Talk to our team")}
          </div>
          <h2 className="font-serif-display text-[36px] md:text-[52px] mt-4 leading-tight">
            {t("Have a question about this?")}
          </h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">
            {t("We're happy to answer questions in the chair — or by phone. No pressure, ever.")}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <a
              data-testid="blog-book-btn"
              href={PRACTICE.appointmentUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
            >
              {t("Request Appointment")} <ArrowUpRight className="w-4 h-4" />
            </a>
            <a
              href={PRACTICE.phoneHref}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white text-white hover:bg-white hover:text-[var(--primary)]"
            >
              <Phone className="w-4 h-4" /> {PRACTICE.phone}
            </a>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="px-6 md:px-10 lg:px-16 py-20">
        <div className="max-w-6xl mx-auto">
          <div className="font-mono-eyebrow text-[var(--text-muted)]">
            {t("More insights")}
          </div>
          <h2 className="font-serif-display text-[var(--primary)] mt-4 text-[32px] md:text-[48px] leading-tight">
            {t("Keep")} <span className="italic text-[var(--accent)]">{t("reading")}</span>.
          </h2>
          <SunDivider className="mt-5" />
          <div className="grid md:grid-cols-3 gap-6 mt-10">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/insights/${p.slug}`}
                data-testid={`related-post-${p.slug}`}
                className="group block bg-[var(--surface)] border border-[var(--border)] rounded-2xl p-6 hover:border-[var(--accent)] transition-colors"
              >
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--text-muted)]">
                  {formatDate(p.publishedAt)}
                </div>
                <div className="font-serif-display text-[22px] mt-2 leading-tight text-[var(--primary)] group-hover:text-[var(--accent)] transition-colors">
                  {p.title}
                </div>
                <p className="mt-3 text-sm text-[var(--text-muted)] line-clamp-3">{p.excerpt}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </article>
  );
}
